import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { legalDocumentsApi } from '@/lib/pillarApi';
import type { LegalDocumentAssignmentPublic } from '@pillar-portal/api-types';
import { SYSTEM_DISPLAY_TIME_ZONE, formatTimestampForMerchantUi } from '@pillar-portal/api-types';
import { LegalAgreementImpactConfirmation } from './LegalAgreementImpactConfirmation';
import {
    PlatformDefaultReplacementSelect,
    type PlatformDefaultReplacement,
} from './PlatformDefaultReplacementSelect';
import { LegalDocumentAssignmentDetailPage } from './LegalDocumentAssignmentDetailPage';

interface LegalDocumentAssignmentsListPageProps {
    readonly initialQueryText?: string;
    readonly legalDocumentUuid?: string;
}

export function LegalDocumentAssignmentsListPage({
    initialQueryText,
    legalDocumentUuid,
}: LegalDocumentAssignmentsListPageProps) {
    const [selectedAssignment, setSelectedAssignment] =
        useState<LegalDocumentAssignmentPublic | null>(null);
    const queryClient = useQueryClient();
    const [revokeError, setRevokeError] = useState<string | null>(null);
    const [revokingAssignment, setRevokingAssignment] =
        useState<LegalDocumentAssignmentPublic | null>(null);
    const [replacement, setReplacement] = useState<PlatformDefaultReplacement | null>(null);
    const revokeMutation = useMutation({
        mutationFn: async (variables: {
            legalDocumentAssignmentUuid: string;
            replacementLegalDocumentUuid: string | null;
        }) =>
            await legalDocumentsApi.revokeLegalDocumentAssignment(
                variables.legalDocumentAssignmentUuid,
                variables.replacementLegalDocumentUuid === null
                    ? {}
                    : { replacement_legal_document_uuid: variables.replacementLegalDocumentUuid }
            ),
        onSuccess: async (revoked) => {
            setRevokeError(null);
            setRevokingAssignment(null);
            setReplacement(null);
            setSelectedAssignment((current) =>
                current?.legal_document_assignment_uuid === revoked.legal_document_assignment_uuid
                    ? revoked
                    : current
            );
            await queryClient.invalidateQueries({ queryKey: ['legalDocumentAssignments'] });
        },
        onError: (error: unknown) => {
            setRevokingAssignment(null);
            setReplacement(null);
            setRevokeError(
                error instanceof Error ? error.message : 'Unable to revoke the assignment.'
            );
        },
    });
    const queryKeyPrefix = useMemo(
        () => ['legalDocumentAssignments', initialQueryText ?? 'all'] as const,
        [initialQueryText]
    );

    return (
        <div className="space-y-4">
            {revokeError === null ? null : (
                <div
                    role="alert"
                    className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                >
                    {revokeError}
                </div>
            )}
            <InfiniteScrollList<LegalDocumentAssignmentPublic, string, typeof queryKeyPrefix>
                pageTitle="Legal document assignments"
                cardTitle="Assignments"
                queryPlaceholder="Search by Organization, title, or version"
                queryKeyPrefix={queryKeyPrefix}
                initialQueryText={initialQueryText}
                pageSize={25}
                fetchPage={async (params) => {
                    const response = await legalDocumentsApi.searchLegalDocumentAssignmentsPage({
                        ...(params.terms === undefined ? {} : { terms: [...params.terms] }),
                        limit: params.limit,
                        cursor: params.cursor,
                    });
                    return {
                        items: response.legal_document_assignments,
                        pagination: response.pagination,
                    };
                }}
                parseCursorParam={(pageParam: unknown) => {
                    if (pageParam === null || pageParam === undefined) {
                        return undefined;
                    }
                    return z.string().parse(pageParam);
                }}
                stateCopy={{
                    loadingLabel: 'Loading assignments...',
                    emptyLabel: 'No assignments found.',
                    endOfListLabel: 'End of assignments.',
                    genericErrorLabel: 'Unable to load legal document assignments.',
                }}
                renderItems={({ items }) => {
                    const visibleItems =
                        legalDocumentUuid === undefined
                            ? items
                            : items.filter(
                                  (assignment) =>
                                      assignment.legal_document.legal_document_uuid ===
                                      legalDocumentUuid
                              );
                    return visibleItems.length === 0 ? (
                        <p className="text-sm text-gray-600">
                            No assignment for this exact document version is present in the loaded
                            results.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {visibleItems.map((assignment) => (
                                <div
                                    key={assignment.legal_document_assignment_uuid}
                                    className="rounded border p-4"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <div className="font-medium">
                                                {assignment.legal_document.legal_document_title} (
                                                {assignment.legal_document.legal_document_version})
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                {assignment.organization === null
                                                    ? 'Default (all Organizations)'
                                                    : `Organization: ${assignment.organization.organization_name}`}
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                Effective:{' '}
                                                {formatTimestampForMerchantUi(
                                                    assignment.in_force_start,
                                                    SYSTEM_DISPLAY_TIME_ZONE
                                                )}{' '}
                                                -{' '}
                                                {assignment.in_force_end === null
                                                    ? 'No end date'
                                                    : formatTimestampForMerchantUi(
                                                          assignment.in_force_end,
                                                          SYSTEM_DISPLAY_TIME_ZONE
                                                      )}
                                            </div>
                                        </div>
                                        <StatusBadge status={assignment.status} />
                                    </div>
                                    {assignment.revocation === null ? null : (
                                        <div className="text-sm text-gray-600">
                                            Revoked on{' '}
                                            {formatTimestampForMerchantUi(
                                                assignment.revocation.revoked_ts,
                                                SYSTEM_DISPLAY_TIME_ZONE
                                            )}
                                            {assignment.revocation.revoke_reason === null
                                                ? ''
                                                : `: ${assignment.revocation.revoke_reason}`}
                                        </div>
                                    )}
                                    <div className="mt-3 flex gap-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => setSelectedAssignment(assignment)}
                                        >
                                            Details
                                        </Button>
                                        {assignment.status === 'ACTIVE' ? (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                disabled={revokeMutation.isPending}
                                                onClick={() => {
                                                    setRevokeError(null);
                                                    setRevokingAssignment(assignment);
                                                }}
                                            >
                                                Revoke
                                            </Button>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                        </div>
                    );
                }}
            />
            {selectedAssignment === null ? null : (
                <LegalDocumentAssignmentDetailPage assignment={selectedAssignment} />
            )}
            <LegalAgreementImpactConfirmation
                request={
                    revokingAssignment === null
                        ? null
                        : {
                              action: 'revoke',
                              legal_document_assignment_uuid:
                                  revokingAssignment.legal_document_assignment_uuid,
                              ...(replacement === null
                                  ? {}
                                  : {
                                        replacement_legal_document_uuid:
                                            replacement.legalDocumentUuid,
                                    }),
                          }
                }
                title="Revoke this assignment?"
                confirmLabel="Revoke"
                isConfirming={revokeMutation.isPending}
                confirmBlocked={(impact) => impact.leaves_no_platform_default}
                onConfirm={() => {
                    if (revokingAssignment !== null) {
                        revokeMutation.mutate({
                            legalDocumentAssignmentUuid:
                                revokingAssignment.legal_document_assignment_uuid,
                            replacementLegalDocumentUuid: replacement?.legalDocumentUuid ?? null,
                        });
                    }
                }}
                onCancel={() => {
                    setRevokingAssignment(null);
                    setReplacement(null);
                }}
            >
                {(impact) =>
                    impact.leaves_no_platform_default || replacement !== null ? (
                        <PlatformDefaultReplacementSelect
                            replacement={replacement}
                            onChange={setReplacement}
                        />
                    ) : null
                }
            </LegalAgreementImpactConfirmation>
        </div>
    );
}
